import { useMemo, useState } from 'react';
import type { AllowedEmail } from '@inbound/contract';

import {
  useAddAllowedEmailMutation,
  useGetAllowlistQuery,
  useRemoveAllowedEmailMutation,
  useRemoveAllowedEmailsMutation,
} from '../../store/allowlist-api.js';
import { useAppDispatch } from '../../store/hooks.js';
import { messageOf } from '../../store/error-message.js';
import { ConfirmDialog } from '../../ui/ConfirmDialog.js';
import { IconPlus, IconTrash } from '../../ui/icons.js';
import { pushToast } from '../../ui/toast-store.js';
import styles from './AccessPage.module.css';

/** What the confirmation dialog is currently asking about. */
type Pending = { kind: 'one'; entry: AllowedEmail } | { kind: 'many'; entries: AllowedEmail[] };

/**
 * Manage who may sign in (S02).
 *
 * There are no roles: anyone with access can grant or revoke it. The two removal
 * guards are enforced by the API; the UI disables the controls it knows will be
 * refused, so a user is not invited to click something that cannot work.
 *
 * Every removal goes through a confirmation, because there is no undo and the
 * consequence — somebody cannot sign in tomorrow morning — is invisible at the
 * moment of the mistake.
 */
export function AccessPage(): JSX.Element {
  const { data: entries, isLoading, isError, refetch } = useGetAllowlistQuery();
  const [addEmail, { isLoading: adding }] = useAddAllowedEmailMutation();
  const [removeEmail, { isLoading: removingOne }] = useRemoveAllowedEmailMutation();
  const [removeEmails, { isLoading: removingMany }] = useRemoveAllowedEmailsMutation();
  const dispatch = useAppDispatch();

  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [pending, setPending] = useState<Pending | null>(null);

  const isLastEntry = (entries?.length ?? 0) <= 1;

  /**
   * Only rows the API would actually accept are selectable, so the bulk action
   * cannot be assembled into a request that is certain to be refused. Your own
   * entry is excluded here rather than validated later.
   */
  const selectable = useMemo(() => (entries ?? []).filter((entry) => !entry.isSelf), [entries]);

  // Selection survives a refetch, so a poll landing mid-click cannot silently
  // widen it; ids that have gone are dropped rather than sent.
  const chosen = useMemo(
    () => selectable.filter((entry) => selected.has(entry.id)),
    [selectable, selected],
  );

  const allChosen = selectable.length > 0 && chosen.length === selectable.length;
  // Removing every selectable row would leave only your own entry, which the API
  // allows — the list keeps one. It is the *whole* list going that is refused.
  const wouldEmptyList = entries !== undefined && chosen.length >= entries.length;

  function toggleRow(id: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll(checked: boolean): void {
    setSelected(checked ? new Set(selectable.map((entry) => entry.id)) : new Set());
  }

  async function onAdd(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;

    try {
      await addEmail({ email: trimmed, ...(note.trim() ? { note: note.trim() } : {}) }).unwrap();
      dispatch(pushToast('success', `${trimmed} can now sign in`));
      setEmail('');
      setNote('');
    } catch (error) {
      // The API's message names the allowed domains on a domain failure, which is
      // more useful than anything this component could invent.
      dispatch(pushToast('error', messageOf(error)));
    }
  }

  async function onConfirm(): Promise<void> {
    if (!pending) return;

    try {
      if (pending.kind === 'one') {
        await removeEmail(pending.entry.id).unwrap();
        dispatch(pushToast('success', `${pending.entry.email} can no longer sign in`));
        setSelected((prev) => {
          const next = new Set(prev);
          next.delete(pending.entry.id);
          return next;
        });
      } else {
        const ids = pending.entries.map((entry) => entry.id);
        const { removed } = await removeEmails({ ids }).unwrap();
        dispatch(
          pushToast('success', `${removed} ${removed === 1 ? 'account' : 'accounts'} removed`),
        );
        setSelected(new Set());
      }
      setPending(null);
    } catch (error) {
      dispatch(pushToast('error', messageOf(error)));
      // Left open on failure: the message explains what to change, and closing
      // would hide it behind a page the user has to act on again.
    }
  }

  return (
    <section className={styles.page}>
      <form className={styles.add} onSubmit={(e) => void onAdd(e)}>
        <div className={styles.fields}>
          <label className={styles.field}>
            <span>Email address</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@company.com"
              autoComplete="off"
              required
            />
          </label>
          <label className={styles.field}>
            <span>
              Note <em>optional</em>
            </span>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Why they need access"
              maxLength={200}
            />
          </label>
        </div>
        <button type="submit" className={styles.primary} disabled={adding || !email.trim()}>
          <IconPlus width="15" height="15" />
          {adding ? 'Granting…' : 'Grant access'}
        </button>
      </form>

      <p className={styles.hint}>
        Adding someone here lets them sign in with their own account — no invitation is sent, and no
        password is ever stored. Their address must also be at an approved domain.
      </p>

      {chosen.length > 0 && (
        <div className={styles.selbar}>
          <span>
            <b>{chosen.length}</b> selected
          </span>
          <button type="button" className={styles.clear} onClick={() => setSelected(new Set())}>
            Clear
          </button>
          <button
            type="button"
            className={styles.bulkDelete}
            disabled={wouldEmptyList}
            title={
              wouldEmptyList
                ? 'That would empty the access list and lock everyone out'
                : `Remove ${chosen.length} accounts`
            }
            onClick={() => setPending({ kind: 'many', entries: chosen })}
          >
            <IconTrash width="15" height="15" />
            Delete
          </button>
        </div>
      )}

      <div className={styles.panel}>
        {isLoading && <div className={styles.empty}>Loading the access list…</div>}

        {isError && (
          <div className={styles.empty}>
            <p>Could not load the access list.</p>
            <button type="button" className={styles.ghost} onClick={() => void refetch()}>
              Try again
            </button>
          </div>
        )}

        {entries && entries.length > 0 && (
          <div className={styles.tableWrap}>
            <table>
              <thead>
                <tr>
                  <th className={styles.pick}>
                    <input
                      type="checkbox"
                      checked={allChosen}
                      // Some but not all: the box shows a dash rather than
                      // claiming either state.
                      ref={(node) => {
                        if (node) node.indeterminate = chosen.length > 0 && !allChosen;
                      }}
                      disabled={selectable.length === 0}
                      onChange={(e) => toggleAll(e.target.checked)}
                      aria-label="Select all removable accounts"
                    />
                  </th>
                  <th>Email</th>
                  <th>Note</th>
                  <th>Added by</th>
                  <th>Added</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => {
                  const blocked = entry.isSelf || isLastEntry;
                  const isChosen = selected.has(entry.id);
                  return (
                    <tr key={entry.id} className={isChosen ? styles.chosen : undefined}>
                      <td className={styles.pick}>
                        <input
                          type="checkbox"
                          checked={isChosen}
                          disabled={entry.isSelf}
                          onChange={() => toggleRow(entry.id)}
                          aria-label={`Select ${entry.email}`}
                          title={entry.isSelf ? 'You cannot remove your own access' : undefined}
                        />
                      </td>
                      <td>
                        <span className={styles.email}>{entry.email}</span>
                        {entry.isSelf && <span className={styles.you}>you</span>}
                      </td>
                      <td className={styles.muted}>{entry.note ?? '—'}</td>
                      <td className={styles.muted}>
                        <AddedBy by={entry.addedBy} />
                      </td>
                      <td className={styles.muted}>
                        {new Date(entry.createdAt).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </td>
                      <td className={styles.actions}>
                        <button
                          type="button"
                          className={styles.rowAct}
                          disabled={blocked}
                          title={
                            entry.isSelf
                              ? 'You cannot remove your own access — ask a colleague'
                              : isLastEntry
                                ? 'The last entry cannot be removed; it would lock everyone out'
                                : `Remove ${entry.email}`
                          }
                          onClick={() => setPending({ kind: 'one', entry })}
                          // An attribute, not a visually-hidden span. `.sr-only`
                          // is absolutely positioned against `#root`, so inside
                          // this horizontally-scrolling table it escaped the
                          // scroller and pushed the page's scroll width out by
                          // the width of the hidden text.
                          aria-label={`Remove ${entry.email}`}
                        >
                          <IconTrash width="16" height="16" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {entries && entries.length === 0 && (
          <div className={styles.empty}>
            {/* Practically unreachable: you had to sign in to get here, so your own
                entry exists. Rendered anyway rather than showing a bare table. */}
            <p>The access list is empty.</p>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={pending !== null}
        title={pending?.kind === 'many' ? 'Remove these accounts?' : 'Remove this account?'}
        confirmLabel={pending?.kind === 'many' ? `Delete ${pending.entries.length}` : 'Remove'}
        confirmIcon={<IconTrash />}
        busy={removingOne || removingMany}
        onConfirm={() => void onConfirm()}
        onCancel={() => setPending(null)}
        body={
          pending === null ? null : pending.kind === 'one' ? (
            <>
              <strong>{pending.entry.email}</strong> will no longer be able to sign in. Nothing else
              about their account changes, and you can grant access again at any time.
            </>
          ) : (
            <>
              <strong>{pending.entries.length} accounts</strong> will no longer be able to sign in:{' '}
              {pending.entries
                .slice(0, 3)
                .map((entry) => entry.email)
                .join(', ')}
              {pending.entries.length > 3 && ` and ${pending.entries.length - 3} more`}. You can
              grant access again at any time.
            </>
          )
        }
      />
    </section>
  );
}

/**
 * Who granted this entry.
 *
 * A null `addedBy` means nobody did: the row came from the `AUTH_BOOTSTRAP_EMAILS`
 * seed, which runs on every deploy and has no user to attribute it to. Naming
 * that "System" is honest, where showing an empty cell would look like data was
 * missing.
 *
 * For a real person the name is shown and the address is one hover away — the
 * name is what a colleague recognises, and the address is what distinguishes two
 * people who share one.
 */
function AddedBy({ by }: { by: AllowedEmail['addedBy'] }): JSX.Element {
  if (by === null) return <span className={styles.system}>System</span>;
  return <span title={by.email}>{by.name ?? by.email}</span>;
}
